// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import client from '@Client';
import useValidateForm from '@Hooks/useValidateForm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import THSelector from '@Molecules/THSelector/THSelector';
import { notifierLogos } from '@Pages/Admin/Organisations/Details/Notifications/List';
import { useGenericQuery } from '@Queries/genericQueries';
import { Form, Space } from 'antd';
import React, { ReactElement, useContext, useMemo, useState } from 'react';
import {
    AuthType,
    httpMethods,
    modeType,
    NotificationTriggerOutput,
    NotifierOutput,
    notifierToWebhookType,
    SSLConfigInput,
    WebhookListOutput,
    WebhookOutput,
    WsConfigInput,
} from 'thehive-sdk';
import { EndpointManage } from '../EndpointManage/EndpointManage';
import { AuthFormItems } from '../Macros/AuthFormItems';
import _ from 'lodash';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';
import { HeadersFormItems } from '../Macros/HeadersFormItems';
import { ICONS } from '@Constants/iconsApp';
import { notifierList } from '@Constants/notification';
import { useLocalMessage } from '@Helpers/locale';
import { StatusContext } from '@Context/StatusProvider';
import { ScopeContext } from '@Context/ScopeProvider';
import { useListAllFields } from '@Queries/describeQueries';
import { TemplateHelpType } from '@Molecules/Inputs/THTextArea/Helper';
import WsConfigForm from '@Organisms/FormConfig/WsConfigForm/WsConfigForm';
import SSLConfigForm from '@Organisms/FormConfig/SSLConfigForm/SSLConfigForm';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import {
    computeNotifierSSLConfig,
    computeNotifierWsConfig,
    computeSSLConfig,
    computeWsConfig,
} from '@Helpers/wsConfig';

import './NotifierManage.scss';

const makeURL = (
    endpoint: string,
    endpointList: WebhookOutput[],
    url: string | null,
    endpointURLAsPrefix: boolean,
): string | null => {
    const endpointURL = endpointList.find(({ name }) => name === endpoint)?.url;

    let processedURL = url?.trim();

    if (endpointURLAsPrefix) {
        const hasSlash = endpointURL?.endsWith('/') || processedURL?.startsWith('/');
        processedURL = hasSlash
            ? `${endpointURL || ''}${processedURL || ''}`
            : `${endpointURL || ''}/${processedURL || ''}`;
    }

    return processedURL?.trim() || null;
};

const extractURL = (endpoint: string, endpointList: WebhookOutput[], url: string, endpointURLAsPrefix: boolean) => {
    const endpointURL = endpointList.find(({ name }) => name === endpoint)?.url;

    let processedURL = url;

    if (endpointURLAsPrefix) {
        processedURL = processedURL?.replace(endpointURL || '', '') || url;
    }

    return processedURL.trim();
};

type enableFormatType = {
    [key in NotifierOutput['name']]: modeType[];
};

const formatListByEndpoint: enableFormatType = {
    Slack: ['markdown', 'json', 'plain_text'],
    Teams: ['markdown', 'json', 'plain_text'],
    EmailerToUser: ['html', 'plain_text'],
    EmailerToAddr: ['html', 'plain_text'],
    Mattermost: ['markdown', 'json', 'plain_text'],
    Webhook: ['json'],
    HttpRequest: ['json', 'xml', 'plain_text'],
    RunAnalyzer: [],
    RunResponder: [],
    Redis: [],
    Kafka: [],
};

interface NotifierFormValues {
    name?: NotifierOutput['name'];
    subject?: string;
    from?: string;
    to?: string;
    template?: string;
    analyzerNames?: {
        [key: string]: string[];
    };
    responderNames?: {
        [key: string]: string[];
    };
    endpoint?: string;
    method?: string;
    url?: string | null;
    bodyTemplate?: string;
    auth?: AuthType;
    logErrors?: boolean;
    headers?: {
        key: string;
        value: string;
    }[];
    wsConfig?: WsConfigInput;
    ssl?: boolean;
    sslConfig?: SSLConfigInput;
    properties?: {
        'bootstrap.servers': string;
    };
    slackAdvancedSettings?: boolean;
    urlPrefixSettings?: boolean;
    templateFormat?: modeType;
    textTemplate?: string;
    adaptiveCardTemplate?: string;
    username?: string;
    password?: string;
    database?: string;
}

interface NotifierManageProps {
    onFinish: (notifier: NotifierFormValues) => void;
    triggerElem?: ReactElement;
    visible?: boolean;
    name: NotifierOutput['name'];
    defaultValues?: NotifierOutput;
    onClose?: () => void;
    onVisibleChange?: (visible: boolean) => void;
    trigger?: NotificationTriggerOutput;
}

export const NotifierManage = ({
    onFinish,
    triggerElem,
    visible,
    name,
    defaultValues,
    onVisibleChange,
    onClose,
    trigger = 'AnyEvent',
}: NotifierManageProps): ReactElement => {
    const i = useLocalMessage();

    const notifier = useMemo(() => notifierList.find(({ name: notifierName }) => notifierName === name), [name]);

    const { data: describeData } = useListAllFields();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!defaultValues,
        fieldsTouchedInit: !!defaultValues,
    });

    const sslEnabled: boolean = Form.useWatch('ssl', form);

    const [templateFormat, setTemplateFormat] = useState<modeType | undefined>(
        defaultValues?.templateFormat || formatListByEndpoint[name]?.find(Boolean) || 'json',
    );
    const { licenseCapabilities } = useContext(StatusContext);

    const notificationCapability = useMemo<string[]>(
        () => Object.keys(licenseCapabilities.notification || {}),
        [licenseCapabilities],
    );

    const { responderList, analyzerList: analyzerData } = useContext(ScopeContext);

    const { data: webhookEndpointList, refetch: refetchWebhookEndpointList } = useGenericQuery<WebhookListOutput>({
        fetchCallback: () => client.NotificationController.listEndpoints(),
        key: ['get-all-organisation-endpoints'],
        manual: true,
        fetchOnStart: true,
    });

    const analyzerDataTypeList = Array.from(
        new Set(analyzerData.reduce((acc, { dataTypeList }) => [...acc, ...dataTypeList], [] as string[])),
    );

    const analyzerNames = useMemo(
        () =>
            analyzerDataTypeList.reduce(
                (acc, dataType) => ({
                    ...acc,
                    [dataType]: analyzerData
                        .filter(({ dataTypeList }) => dataTypeList.includes(dataType))
                        .map(({ name }) => name),
                }),
                {} as { [key: string]: string[] },
            ),
        [analyzerData, analyzerDataTypeList],
    );

    const responderTypeList = Array.from(
        new Set(responderList.reduce((acc, { dataTypeList }) => [...acc, ...dataTypeList], [] as string[])),
    );

    const responderNames = useMemo(
        () =>
            responderTypeList.reduce(
                (acc, dataType) => ({
                    ...acc,
                    [dataType]: responderList
                        .filter(({ dataTypeList }) => dataTypeList.includes(dataType))
                        .map(({ name }) => name),
                }),
                {} as { [key: string]: string[] },
            ),
        [responderTypeList, responderList],
    );

    const onFinishHandler = ({ headers, properties, urlPrefixSettings, ...values }: NotifierFormValues) => {
        onFinish({
            ...values,
            urlPrefixSettings,
            templateFormat,
            properties: !_.isEmpty(properties?.['bootstrap.servers']) ? properties : undefined,
            headers: headers?.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {}) as any,
            url:
                makeURL(values.endpoint || '', endpointList, values.url || null, urlPrefixSettings || false) ||
                undefined,
            adaptiveCardTemplate: values.adaptiveCardTemplate ? values.adaptiveCardTemplate : undefined,
        });
    };

    const endpointList = useMemo(
        () => [...(webhookEndpointList?.organisation || []), ...(webhookEndpointList?.global || [])] || [],
        [webhookEndpointList],
    );

    const endpointNames = useMemo(() => endpointList.map(({ name }) => name), [endpointList]);

    const extraHelp: TemplateHelpType[] = useMemo(() => {
        if (!describeData) {
            return [];
        }

        if (['CaseClosed', 'CaseShared', 'CaseCreated'].includes(trigger)) {
            return describeData?.case.attributes.map(({ name }) => ({
                name: `object.${name.replace('customFields', 'customFieldValues')}`,
            }));
        }

        if (['LogInMyTask', 'TaskAssigned', 'TaskClosed'].includes(trigger)) {
            return [
                ...(describeData?.task.attributes.map(({ name }) => ({
                    name: `object.${name}`,
                })) || []),
                { name: 'object.case' },
                ...(describeData?.case.attributes.map(({ name }) => ({
                    name: `context.${name}`,
                })) || []),
            ];
        }

        if (['ObservableCreated'].includes(trigger)) {
            return [
                ...(describeData?.observable.attributes.map(({ name }) => ({
                    name: `object.${name}`,
                })) || []),
                { name: 'object.case' },
                { name: 'object.alert' },
                ...Array.from(
                    new Set([...(describeData?.case.attributes || []), ...(describeData?.alert.attributes || [])]),
                ).map(({ name }) => ({
                    name: `context.${name}`,
                })),
            ];
        }

        if (['AlertCreated', 'AlertImported', 'AlertAssigned', 'AlertClosed'].includes(trigger)) {
            return describeData?.alert.attributes.map(({ name }) => ({
                name: `object.${name.replace('customFields', 'customFieldValues')}`,
            }));
        }

        return [];
    }, [trigger, describeData]);

    return (
        <THDrawer
            title={
                <Space size={3}>
                    <span className="notifier-manage__drawer__title_logo">{notifierLogos[name]}</span>
                    {notifier?.label || name}
                </Space>
            }
            triggerElem={triggerElem}
            visible={visible}
            onChange={onVisibleChange}
            className="drawer-form"
            key="notifier-drawer"
            destroyOnClose
            onClose={onClose}
            disableClose={fieldsTouched}
            afterVisibleChange={() => {
                form.resetFields();

                if (name === 'Webhook' || name === 'HttpRequest') {
                    refetchWebhookEndpointList();
                }

                onFormValuesChange();
            }}
            width="50vw"
        >
            {(toggleDrawer) => (
                <THForm<NotifierFormValues>
                    name="notifier-form"
                    key="notifier-form"
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    initialValues={{
                        logErrors: name === 'HttpRequest' ? true : undefined,
                        auth: { type: 'none' },
                        ...defaultValues,
                        headers:
                            name === 'HttpRequest'
                                ? Object.entries((defaultValues as any)?.headers || {})?.map(([key, value]) => ({
                                      key,
                                      value,
                                  }))
                                : [],
                        url: extractURL(
                            (defaultValues as any)?.endpoint || '',
                            endpointList,
                            (defaultValues as any)?.url || '',
                            (defaultValues as any)?.urlPrefixSettings || false,
                        ),
                        wsConfig: computeNotifierWsConfig(name, defaultValues),
                        sslConfig: computeNotifierSSLConfig(name, defaultValues),
                    }}
                    onFinish={(values) => {
                        const wsConfig = values.wsConfig && computeWsConfig(values.wsConfig);
                        const sslConfig = values.sslConfig && computeSSLConfig(values.sslConfig);

                        toggleDrawer();
                        onFinishHandler({ name, ...values, wsConfig, sslConfig });
                        setFieldsTouched(false);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                onCancel={() => {
                                    toggleDrawer();
                                    onClose?.();
                                    setFieldsTouched(false);
                                    form.resetFields();
                                }}
                                formName="notifier-form"
                                disabled={!isValid || !fieldsTouched}
                            />
                        }
                    >
                        <THForm.Item
                            name="endpoint"
                            label={i('notification.notifiers.endpoint.label')}
                            hidden={!['Webhook', 'Mattermost', 'Slack', 'Teams', 'HttpRequest'].includes(name)}
                            rules={[
                                {
                                    required: ['Webhook', 'Mattermost', 'Slack', 'Teams'].includes(name),
                                    message: i('notification.notifiers.endpoint.required'),
                                },
                            ]}
                            extraAction={
                                <EndpointManage
                                    disableTypeChoose
                                    endpointNames={endpointNames}
                                    defaultValues={{ type: notifierToWebhookType[name] }}
                                    notificationCapability={notificationCapability}
                                    canEditOrganisation={false}
                                    onVisibleChange={(visible) => {
                                        if (visible) {
                                            setFieldsTouched(true);
                                        }
                                    }}
                                    onFinish={({ filterOrganisation, type, ...values }) =>
                                        client.NotificationController.addOrganisationEndpoint({
                                            ...values,
                                            type: type?.name,
                                            includedTheHiveOrganisations:
                                                filterOrganisation === 'all'
                                                    ? ['*']
                                                    : values.includedTheHiveOrganisations,
                                        }).then(() =>
                                            refetchWebhookEndpointList().then(() => {
                                                form.setFieldsValue({ endpoint: values.name });
                                                onFormValuesChange();
                                            }),
                                        )
                                    }
                                    triggerElem={<THButton type="link">Add a new endpoint</THButton>}
                                />
                            }
                        >
                            <THSelect
                                options={endpointList
                                    .filter(({ type }) => notifierToWebhookType[name] === type)
                                    .map(({ name }) => ({
                                        label: name,
                                        value: name,
                                    }))}
                                allowClear
                                onDropdownVisibleChange={(open) => open && refetchWebhookEndpointList()}
                                placeholder={i('notification.notifiers.endpoint.placeholder')}
                            />
                        </THForm.Item>

                        <THInput.Helper
                            name="subject"
                            label={i('notification.notifiers.subject.label')}
                            hidden={!['EmailerToUser', 'EmailerToAddr'].includes(name)}
                            rules={[
                                {
                                    required: ['EmailerToUser', 'EmailerToAddr'].includes(name),
                                    message: i('notification.notifiers.subject.required'),
                                },
                            ]}
                            placeholder={i('notification.notifiers.subject.placeholder')}
                            onChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                            extraFields={extraHelp}
                        />

                        <THInput.Helper
                            name="from"
                            label={i('notification.notifiers.from.label')}
                            hidden={!['EmailerToUser', 'EmailerToAddr'].includes(name)}
                            rules={[
                                {
                                    required: ['EmailerToUser', 'EmailerToAddr'].includes(name),
                                    message: i('notification.notifiers.from.required'),
                                },
                            ]}
                            placeholder={i('notification.notifiers.from.placeholder')}
                            onChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                            extraFields={extraHelp}
                        />

                        <THInput.Helper
                            name="to"
                            label={i('notification.notifiers.to.label')}
                            hidden={name !== 'EmailerToAddr'}
                            rules={[
                                {
                                    required: name === 'EmailerToAddr',
                                    message: i('notification.notifiers.to.required'),
                                },
                            ]}
                            placeholder={i('notification.notifiers.to.placeholder')}
                            onChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                            extraFields={extraHelp}
                        />

                        <THForm.Item<NotifierFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.slackAdvancedSettings !== next.slackAdvancedSettings}
                        >
                            <THTextArea.Helper
                                name="textTemplate"
                                label={i('notification.notifiers.slack.text-template.label')}
                                onChangeFormat={setTemplateFormat}
                                valueFormat={templateFormat}
                                formatList={formatListByEndpoint[name] || []}
                                hidden={name !== 'Slack'}
                                rules={[
                                    {
                                        required: name === 'Slack',
                                        message: i('notification.notifiers.slack.text-template.required'),
                                    },
                                ]}
                                extraFields={extraHelp}
                            />
                        </THForm.Item>

                        <THForm.Item<NotifierFormValues> noStyle>
                            <THTextArea.Helper
                                name="textTemplate"
                                label={i('notification.notifiers.teams.text-template.label')}
                                onChangeFormat={setTemplateFormat}
                                hidden={name !== 'Teams'}
                                rules={[
                                    {
                                        required: name === 'Teams',
                                        message: i('notification.notifiers.teams.text-template.required'),
                                    },
                                ]}
                                extraFields={extraHelp}
                            />
                        </THForm.Item>

                        <THInput.Helper
                            name="channel"
                            label={i('notification.notifiers.channel.label')}
                            hidden={!['Slack', 'Mattermost', 'Redis'].includes(name)}
                            rules={[
                                {
                                    required: ['Mattermost', 'Redis'].includes(name),
                                    message: i('notification.notifiers.channel.required'),
                                },
                            ]}
                            placeholder={i('notification.notifiers.channel.placeholder')}
                            onChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                            extraFields={extraHelp}
                        />

                        <THInput.Helper
                            name="host"
                            label={i('notification.notifiers.host.label')}
                            hidden={name !== 'Redis'}
                            rules={[
                                {
                                    required: name === 'Redis',
                                    message: i('notification.notifiers.host.required'),
                                    // validator: urlValidator,
                                },
                            ]}
                            placeholder={i('notification.notifiers.host.placeholder')}
                            onChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                            extraFields={extraHelp}
                        />

                        <THForm.Item
                            name="port"
                            label={i('notification.notifiers.port.label')}
                            hidden={name !== 'Redis'}
                            rules={[
                                {
                                    required: name === 'Redis',
                                    message: i('notification.notifiers.port.required'),
                                },
                            ]}
                        >
                            <THInputNumber placeholder={i('notification.notifiers.port.placeholder')} />
                        </THForm.Item>

                        <THInput.Helper
                            name="username"
                            label={i('notification.notifiers.username.label')}
                            hidden={!['Slack', 'Mattermost', 'Redis'].includes(name)}
                            rules={[
                                {
                                    required: ['Mattermost'].includes(name),
                                    message: i('notification.notifiers.username.required'),
                                },
                            ]}
                            placeholder={i('notification.notifiers.username.placeholder')}
                            onChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                            extraFields={extraHelp}
                            autoComplete="new-password"
                        />

                        <THForm.Item
                            name="password"
                            label={i('notification.notifiers.password.label')}
                            hidden={!['Redis'].includes(name)}
                        >
                            <THInput
                                type="password"
                                placeholder={i('notification.notifiers.password.placeholder')}
                                onChange={() => {
                                    onFormValuesChange();
                                    setFieldsTouched(true);
                                }}
                                autoComplete="new-password"
                            />
                        </THForm.Item>

                        <THForm.Item
                            name="database"
                            label={i('notification.notifiers.database.label')}
                            hidden={!['Redis'].includes(name)}
                        >
                            <THInputNumber
                                placeholder={i('notification.notifiers.database.placeholder')}
                                onChange={() => {
                                    onFormValuesChange();
                                    setFieldsTouched(true);
                                }}
                                autoComplete="new-password"
                            />
                        </THForm.Item>

                        {['Redis'].includes(name) && (
                            <>
                                <THForm.Item name="ssl" label={i('sslConfig.field.enableSSL')} valuePropName="checked">
                                    <THSwitch />
                                </THForm.Item>
                                {sslEnabled && <SSLConfigForm form={form} name={['sslConfig']} />}
                            </>
                        )}

                        <THTextArea.Helper
                            name="adaptiveCardTemplate"
                            label={i('notification.notifiers.teams.adaptive-card-template.label')}
                            hidden={name !== 'Teams'}
                            placeholder={i('notification.notifiers.teams.adaptive-card-template.placeholder')}
                            extraFields={extraHelp}
                        />

                        {name === 'Slack' && (
                            <TitleSection className="notifier-manage__title-section">
                                <Space>
                                    {i('notification.notifiers.slack.advanced-settings')}
                                    <THForm.Item name="slackAdvancedSettings" valuePropName="checked">
                                        <THCheckbox />
                                    </THForm.Item>
                                </Space>
                            </TitleSection>
                        )}

                        <THForm.Item<NotifierFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.slackAdvancedSettings !== next.slackAdvancedSettings}
                        >
                            {({ getFieldValue }) => (
                                <>
                                    {getFieldValue('slackAdvancedSettings') && (
                                        <>
                                            <span className="notifier-manage__slack-help">
                                                {i('notification.notifiers.slack.documentation.help')}{' '}
                                                <ExternalLink url="https://api.slack.com/methods/chat.postMessage">
                                                    {i('notification.notifiers.slack.documentation.slack')}
                                                </ExternalLink>
                                            </span>
                                            <THTextArea.Helper
                                                name="blocksTemplate"
                                                label={i('notification.notifiers.slack.blocks-template.label')}
                                                placeholder={i(
                                                    'notification.notifiers.slack.blocks-template.placeholder',
                                                )}
                                                extraFields={extraHelp}
                                            />

                                            <THTextArea.Helper
                                                name="attachmentsTemplate"
                                                label={i('notification.notifiers.slack.attachments-template.label')}
                                                placeholder={i(
                                                    'notification.notifiers.slack.attachments-template.placeholder',
                                                )}
                                                extraFields={extraHelp}
                                            />

                                            <THForm.Item
                                                name="asUser"
                                                label={i('notification.notifiers.slack.as-user.label')}
                                                valuePropName="checked"
                                            >
                                                <THSwitch />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="iconEmoji"
                                                label={i('notification.notifiers.slack.icon-emoji.label')}
                                            >
                                                <THInput
                                                    placeholder={i(
                                                        'notification.notifiers.slack.icon-emoji.placeholder',
                                                    )}
                                                />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="iconUrl"
                                                label={i('notification.notifiers.slack.icon-url.label')}
                                            >
                                                <THInput
                                                    placeholder={i('notification.notifiers.slack.icon-url.placeholder')}
                                                />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="linkNames"
                                                label={i('notification.notifiers.slack.link-names.label')}
                                                valuePropName="checked"
                                            >
                                                <THSwitch />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="mrkdwn"
                                                label={i('notification.notifiers.slack.mrkdwn.label')}
                                                valuePropName="checked"
                                            >
                                                <THSwitch />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="parse"
                                                label={i('notification.notifiers.slack.parse.label')}
                                            >
                                                <THInput
                                                    placeholder={i('notification.notifiers.slack.parse.placeholder')}
                                                />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="unfurlLinks"
                                                label={i('notification.notifiers.slack.unfurl-links.label')}
                                                valuePropName="checked"
                                            >
                                                <THSwitch />
                                            </THForm.Item>

                                            <THForm.Item
                                                name="unfurlMedia"
                                                label={i('notification.notifiers.slack.unfurl-media.label')}
                                                valuePropName="checked"
                                            >
                                                <THSwitch />
                                            </THForm.Item>
                                        </>
                                    )}
                                </>
                            )}
                        </THForm.Item>

                        <THTextArea.Helper
                            name="template"
                            label={i('notification.notifiers.template.label')}
                            hidden={!['EmailerToUser', 'EmailerToAddr', 'Mattermost'].includes(name)}
                            onChangeFormat={setTemplateFormat}
                            valueFormat={templateFormat}
                            formatList={formatListByEndpoint[name] || []}
                            rules={[
                                {
                                    required: ['EmailerToUser', 'EmailerToAddr', 'Mattermost'].includes(name),
                                    message: i('notification.notifiers.template.required'),
                                },
                            ]}
                            extraFields={extraHelp}
                        />

                        {!!analyzerDataTypeList.length &&
                            analyzerDataTypeList.map((dataType) => (
                                <THForm.Item
                                    key={dataType}
                                    name={['analyzerNames', dataType]}
                                    label={dataType}
                                    hidden={name !== 'RunAnalyzer'}
                                >
                                    <THSelector<string> dataSource={analyzerNames[dataType] || []}>
                                        {(name, selected) => (
                                            <div>
                                                {selected ? (
                                                    <ICONS.CheckCircleTwoTone />
                                                ) : (
                                                    <ICONS.CheckCircleOutlined />
                                                )}{' '}
                                                {name}
                                            </div>
                                        )}
                                    </THSelector>
                                </THForm.Item>
                            ))}

                        {!!responderTypeList.length &&
                            responderTypeList.map((dataType) => (
                                <THForm.Item
                                    key={dataType}
                                    name={['responderNames', dataType]}
                                    label={dataType}
                                    hidden={name !== 'RunResponder'}
                                >
                                    <THSelector<string> dataSource={responderNames[dataType] || []}>
                                        {(name, selected) => (
                                            <div>
                                                {selected ? (
                                                    <ICONS.CheckCircleTwoTone />
                                                ) : (
                                                    <ICONS.CheckCircleOutlined />
                                                )}{' '}
                                                {name}
                                            </div>
                                        )}
                                    </THSelector>
                                </THForm.Item>
                            ))}

                        <THForm.Item<NotifierFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.endpoint !== next.endpoint}
                        >
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name="method"
                                    label={i('notification.notifiers.http-request.method.label')}
                                    hidden={name !== 'HttpRequest'}
                                    rules={[
                                        {
                                            required: name === 'HttpRequest' && !getFieldValue('endpoint'),
                                            message: i('notification.notifiers.http-request.method.required'),
                                        },
                                    ]}
                                >
                                    <THSelect
                                        options={httpMethods.map((method) => ({ label: method, value: method }))}
                                        placeholder={i('notification.notifiers.http-request.method.placeholder')}
                                    />
                                </THForm.Item>
                            )}
                        </THForm.Item>

                        <THForm.Item<NotifierFormValues>
                            noStyle
                            shouldUpdate={(prev, next) =>
                                prev.endpoint !== next.endpoint || prev.urlPrefixSettings !== next.urlPrefixSettings
                            }
                        >
                            {({ getFieldValue }) => (
                                <THInput.Helper
                                    name="url"
                                    label={i('notification.notifiers.http-request.url.label')}
                                    hidden={name !== 'HttpRequest'}
                                    rules={[
                                        {
                                            required: name === 'HttpRequest' && !getFieldValue('endpoint'),
                                            message: i('notification.notifiers.http-request.url.required'),
                                        },
                                    ]}
                                    placeholder={i('notification.notifiers.http-request.url.placeholder')}
                                    onChange={() => {
                                        onFormValuesChange();
                                        setFieldsTouched(true);
                                    }}
                                    extraAction={
                                        getFieldValue('endpoint') && (
                                            <>
                                                <THForm.Item name="urlPrefixSettings" valuePropName="checked">
                                                    <THCheckbox />
                                                </THForm.Item>
                                                {i('notification.notifiers.http-request.url.extra-action')}
                                            </>
                                        )
                                    }
                                    addonBefore={
                                        getFieldValue('urlPrefixSettings') &&
                                        endpointList.find(({ name }) => name === (getFieldValue('endpoint') as string))
                                            ?.url
                                    }
                                    extraFields={extraHelp}
                                />
                            )}
                        </THForm.Item>

                        <THTextArea.Helper
                            name="bodyTemplate"
                            label={i('notification.notifiers.http-request.body-template.label')}
                            hidden={name !== 'HttpRequest'}
                            onChangeFormat={setTemplateFormat}
                            valueFormat={templateFormat}
                            formatList={formatListByEndpoint[name] || []}
                            extraFields={extraHelp}
                        />

                        <THForm.Item
                            name="logErrors"
                            label={i('notification.notifiers.http-request.log-errors.label')}
                            hidden={name !== 'HttpRequest'}
                            valuePropName="checked"
                        >
                            <THSwitch />
                        </THForm.Item>

                        {name === 'HttpRequest' && (
                            <>
                                <HeadersFormItems
                                    onChange={() => {
                                        onFormValuesChange();
                                        setFieldsTouched(true);
                                    }}
                                    notifierName={name}
                                    extraFields={extraHelp}
                                />

                                <AuthFormItems local={i} />

                                <THForm.Item noStyle shouldUpdate>
                                    {({ getFieldValue }) =>
                                        !getFieldValue('endpoint') && <WsConfigForm name={['wsConfig']} form={form} />
                                    }
                                </THForm.Item>
                            </>
                        )}

                        <THForm.Item
                            name="topic"
                            label={i('notification.notifiers.kafka.topic.label')}
                            hidden={name !== 'Kafka'}
                            rules={[
                                {
                                    required: name === 'Kafka',
                                    message: i('notification.notifiers.kafka.topic.required'),
                                },
                            ]}
                        >
                            <THInput placeholder={i('notification.notifiers.kafka.topic.placeholder')} />
                        </THForm.Item>

                        <THForm.Item
                            name={['properties', 'bootstrap.servers']}
                            label={i('notification.notifiers.kafka.bootstrap-servers.label')}
                            hidden={name !== 'Kafka'}
                            rules={[
                                {
                                    required: name === 'Kafka',
                                    message: i('notification.notifiers.kafka.bootstrap-servers.required'),
                                },
                            ]}
                            help={i('notification.notifiers.kafka.bootstrap-servers.help')}
                        >
                            <THInput placeholder={i('notification.notifiers.kafka.bootstrap-servers.placeholder')} />
                        </THForm.Item>
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
